// src/pages/SocialScreen.js

import React, { useState, useEffect, useCallback } from 'react';
import { 
    Share2, Image, Calendar, Clock, Send, Edit, Trash2, 
    Plus, ChevronLeft, ChevronRight, Eye, BarChart3, Loader, RefreshCw,
    ExternalLink, HelpCircle, AlertTriangle
} from 'lucide-react';
import { fromZonedTime } from 'date-fns-tz';
import { api } from '../api';
import toast from 'react-hot-toast';
import '../styles/SocialScreen.css';
import { generateCaption, generatePostHashtags } from '../social/caption-generator';

const SERVER_TIMEZONE = 'America/Los_Angeles';

const SocialScreen = ({ user }) => {
    // --- View State ---
    const [activeTab, setActiveTab] = useState('posts');
    
    // --- Filter State ---
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    
    // --- Data State ---
    const [posts, setPosts] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [stats, setStats] = useState({ total_posts: 0, posted: 0, scheduled: 0, drafts: 0, failed: 0 });
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [facebookUrl, setFacebookUrl] = useState('');
    const [showHelp, setShowHelp] = useState(false);
    const [userTimezone, setUserTimezone] = useState(SERVER_TIMEZONE);
    
    // --- Calendar State ---
    const [calendarDate, setCalendarDate] = useState(new Date());
    
    // --- Modal State ---
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);

    // --- Load Properties (filtered by social permission) ---
    useEffect(() => {
        const loadProperties = async () => {
            try {
                const props = await api.getPropertiesByFeature('social');
                setProperties(props);
            } catch (err) {
                toast.error("Could not load properties");
            }
        };
        loadProperties();
    }, []);

    // --- Load User Timezone Preference ---
    useEffect(() => {
        const loadTimezone = async () => {
            try {
                const prefs = await api.getUserPreferences();
                const safePrefs = Array.isArray(prefs) ? prefs : [];
                const tzPref = safePrefs.find(p => p.preference_key === 'preferred_timezone');
                if (tzPref && tzPref.preference_value) {
                    setUserTimezone(tzPref.preference_value);
                }
            } catch (err) {
                // Use default SERVER_TIMEZONE
            }
        };
        loadTimezone();
    }, []);

    // --- Load Facebook URL when property changes ---
    useEffect(() => {
        const loadFacebookUrl = async () => {
            if (!selectedPropertyId) {
                setFacebookUrl('');
                return;
            }
            try {
                const data = await api.getSocialImages(selectedPropertyId);
                setFacebookUrl(data.facebookUrl || '');
            } catch (err) {
                setFacebookUrl('');
            }
        };
        loadFacebookUrl();
    }, [selectedPropertyId]);

    // --- Load Stats ---
    const loadStats = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (selectedPropertyId) params.append('propertyId', selectedPropertyId);
            
            const data = await api.getSocialStats(params.toString());
            setStats(data);
        } catch (err) {
            console.error("Failed to load stats:", err);
        }
    }, [selectedPropertyId]);

    // --- Load Posts ---
    const loadPosts = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', '20');
            if (selectedPropertyId) params.append('propertyId', selectedPropertyId);
            if (selectedType) params.append('type', selectedType);
            if (selectedStatus) params.append('status', selectedStatus);
            
            const data = await api.getSocialPosts(params.toString());
            setPosts(data.posts || []);
            setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        } catch (err) {
            console.error("Failed to load posts:", err);
            toast.error("Failed to load posts");
        } finally {
            setLoading(false);
        }
    }, [selectedPropertyId, selectedType, selectedStatus]);

    // --- Load Schedules (posts with SCHEDULED status) ---
    const loadSchedules = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (selectedPropertyId) params.append('propertyId', selectedPropertyId);
            params.append('status', 'SCHEDULED');
            params.append('limit', '100');
            
            const data = await api.getSocialPosts(params.toString());
            setSchedules(data.posts || []);
        } catch (err) {
            console.error("Failed to load schedules:", err);
        }
    }, [selectedPropertyId]);

    // --- Initial Load ---
    useEffect(() => {
        loadStats();
        if (activeTab === 'posts') {
            loadPosts();
        } else if (activeTab === 'scheduled') {
            loadSchedules();
        } else if (activeTab === 'calendar') {
            loadSchedules();
        }
    }, [activeTab, loadStats, loadPosts, loadSchedules]);

    // --- Filter Change Handlers ---
    const handlePropertyChange = (e) => {
        setSelectedPropertyId(e.target.value);
    };

    const handleTypeChange = (e) => {
        setSelectedType(e.target.value);
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    // --- Format MySQL datetime using user timezone ---
    const formatDatetime = (mysqlDatetime) => {
        if (!mysqlDatetime) return '-';
        try {
            // Handle both "YYYY-MM-DD HH:mm:ss" and ISO string formats
            let y, mo, da, h, mi, s;
            if (mysqlDatetime.includes('T')) {
                const d = new Date(mysqlDatetime);
                if (isNaN(d.getTime())) return mysqlDatetime;
                y = d.getFullYear();
                mo = d.getMonth() + 1;
                da = d.getDate();
                h = d.getHours();
                mi = d.getMinutes();
                s = d.getSeconds();
            } else {
                const [datePart, timePart] = mysqlDatetime.split(' ');
                [y, mo, da] = datePart.split('-').map(Number);
                [h, mi, s] = (timePart || '00:00:00').split(':').map(Number);
            }
            // Parse as server timezone (LA), format in user timezone
            const laDate = fromZonedTime(new Date(y, mo - 1, da, h, mi, s || 0), SERVER_TIMEZONE);
            if (isNaN(laDate.getTime())) return mysqlDatetime;
            return laDate.toLocaleString('en-US', {
                timeZone: userTimezone,
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return mysqlDatetime;
        }
    };

    const formatDateOnly = (mysqlDatetime) => {
        if (!mysqlDatetime) return '-';
        try {
            let y, mo, da, h, mi, s;
            if (mysqlDatetime.includes('T')) {
                const d = new Date(mysqlDatetime);
                if (isNaN(d.getTime())) return mysqlDatetime;
                y = d.getFullYear();
                mo = d.getMonth() + 1;
                da = d.getDate();
                h = d.getHours();
                mi = d.getMinutes();
                s = d.getSeconds();
            } else {
                const [datePart, timePart] = mysqlDatetime.split(' ');
                [y, mo, da] = datePart.split('-').map(Number);
                [h, mi, s] = (timePart || '00:00:00').split(':').map(Number);
            }
            const laDate = fromZonedTime(new Date(y, mo - 1, da, h, mi, s || 0), SERVER_TIMEZONE);
            if (isNaN(laDate.getTime())) return mysqlDatetime;
            return laDate.toLocaleString('en-US', {
                timeZone: userTimezone,
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (e) {
            return mysqlDatetime;
        }
    };

    // --- Post Actions ---
    const handleViewPost = (post) => {
        setSelectedPost(post);
        setShowDetailModal(true);
    };

    const handleEditPost = (post) => {
        setSelectedPost(post);
        setShowEditModal(true);
    };

    const handleDeletePost = (post) => {
        setPostToDelete(post);
        setShowDeleteModal(true);
    };

    const confirmDeletePost = async () => {
        if (!postToDelete) return;
        
        try {
            await api.deleteSocialPost(postToDelete.id);
            toast.success("Post deleted");
            setShowDeleteModal(false);
            setPostToDelete(null);
            loadPosts(pagination.page);
            loadSchedules();
            loadStats();
        } catch (err) {
            toast.error("Failed to delete post");
        }
    };

    const handleCreatePost = () => {
        setShowCreateModal(true);
    };

    const handlePostCreated = () => {
        setShowCreateModal(false);
        loadPosts(1);
        loadSchedules();
        loadStats();
        toast.success("Post created successfully");
    };

    // --- Calendar Navigation ---
    const prevMonth = () => {
        setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
    };

    // --- Render Stats Cards ---
    const renderStats = () => (
        <div className="social-stats-grid">
            <div className="stat-card">
                <div className="stat-icon total"><BarChart3 size={24} /></div>
                <div className="stat-info">
                    <h3>{stats.total_posts || 0}</h3>
                    <p>Total Posts</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon scheduled"><Clock size={24} /></div>
                <div className="stat-info">
                    <h3>{stats.scheduled || 0}</h3>
                    <p>Scheduled</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon posted"><Send size={24} /></div>
                <div className="stat-info">
                    <h3>{stats.posted || 0}</h3>
                    <p>Posted</p>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-icon failed"><Trash2 size={24} /></div>
                <div className="stat-info">
                    <h3>{stats.failed || 0}</h3>
                    <p>Failed</p>
                </div>
            </div>
        </div>
    );

    // --- Render Filter Bar ---
    const renderFilterBar = () => (
        <div className="social-filter-bar">
            <div className="filter-group">
                <label>Property</label>
                <select value={selectedPropertyId} onChange={handlePropertyChange}>
                    <option value="">All Properties</option>
                    {properties.map(prop => (
                        <option key={prop.id} value={prop.id}>{prop.name}</option>
                    ))}
                </select>
            </div>
            
            {activeTab === 'posts' && (
                <>
                    <div className="filter-group">
                        <label>Post Type</label>
                        <select value={selectedType} onChange={handleTypeChange}>
                            <option value="">All Types</option>
                            <option value="ROOM">Room</option>
                            <option value="LOBBY">Lobby</option>
                            <option value="OFFER">Offer</option>
                            <option value="ATTRACTION">Attraction</option>
                            <option value="REVIEW">Review</option>
                            <option value="SEASONAL">Seasonal</option>
                        </select>
                    </div>
                    
                    <div className="filter-group">
                        <label>Status</label>
                        <select value={selectedStatus} onChange={handleStatusChange}>
                            <option value="">All Status</option>
                            <option value="DRAFT">Draft</option>
                            <option value="SCHEDULED">Scheduled</option>
                            <option value="POSTED">Posted</option>
                            <option value="FAILED">Failed</option>
                        </select>
                    </div>
                </>
            )}
            
            <div className="filter-actions">
                {facebookUrl && (
                    <a 
                        href={facebookUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="facebook-link-btn"
                    >
                        <ExternalLink size={16} />
                        View on Facebook
                    </a>
                )}
                <button 
                    className="help-btn"
                    onClick={() => setShowHelp(!showHelp)}
                >
                    <HelpCircle size={16} />
                </button>
                <button className="create-post-btn" onClick={handleCreatePost}>
                    <Plus size={18} />
                    Create Post
                </button>
            </div>
        </div>
    );

    // --- Render Posts Table ---
    const renderPostsTable = () => {
        if (loading) {
            return <div className="social-loading">Loading posts...</div>;
        }
        
        if (posts.length === 0) {
            return (
                <div className="social-empty-state">
                    <Image size={48} className="icon" />
                    <h3>No posts yet</h3>
                    <p>Create your first social media post to get started.</p>
                    <button className="create-post-btn" onClick={handleCreatePost}>
                        <Plus size={18} />
                        Create Post
                    </button>
                </div>
            );
        }
        
        return (
            <div className="social-table-wrapper">
            <table className="social-posts-table">
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Property</th>
                        <th>Type</th>
                        <th>Caption</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {posts.map(post => (
                        <tr key={post.id}>
                            <td>
                                <img 
                                    src={post.image_path?.startsWith('[') ? JSON.parse(post.image_path)[0] : post.image_path} 
                                    alt="" 
                                    className="post-thumbnail"
                                    onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                                />
                            </td>
                            <td>{post.property_name}</td>
                            <td>
                                <span className={`calendar-event ${post.post_type.toLowerCase()}`}>
                                    {post.post_type}
                                </span>
                            </td>
                            <td>
                                <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {post.caption}
                                </div>
                            </td>
                            <td>
                                <span 
                                    className={`status-badge ${post.status.toLowerCase()}`}
                                    title={
                                        post.status === 'DRAFT' ? 'Saved but not published. Edit or delete anytime.' :
                                        post.status === 'SCHEDULED' ? 'Will publish automatically at the scheduled date/time.' :
                                        post.status === 'POSTED' ? 'Published to Facebook.' :
                                        post.status === 'FAILED' ? 'Something went wrong. Edit and reschedule.' :
                                        ''
                                    }
                                >
                                    {post.status}
                                </span>
                            </td>
                            <td>
                                <div className="action-buttons">
                                    <button 
                                        className="action-btn view"
                                        onClick={() => handleViewPost(post)}
                                    >
                                        <Eye size={14} />
                                    </button>
                                    {post.status !== 'POSTED' && (
                                        <button 
                                            className="action-btn edit"
                                            onClick={() => handleEditPost(post)}
                                        >
                                            <Edit size={14} />
                                        </button>
                                    )}
                                    <button 
                                        className="action-btn delete"
                                        onClick={() => handleDeletePost(post)}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        );
    };

    // --- Render Pagination ---
    const renderPagination = () => {
        if (pagination.pages <= 1) return null;
        
        return (
            <div className="social-pagination">
                <button 
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => loadPosts(pagination.page - 1)}
                >
                    <ChevronLeft size={16} />
                </button>
                
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                    .filter(page => {
                        const diff = Math.abs(page - pagination.page);
                        return diff === 0 || diff === 1 || page === 1 || page === pagination.pages;
                    })
                    .map((page, index, arr) => (
                        <React.Fragment key={page}>
                            {index > 0 && arr[index - 1] !== page - 1 && (
                                <span style={{ color: '#9ca3af' }}>...</span>
                            )}
                            <button 
                                className={`pagination-btn ${page === pagination.page ? 'active' : ''}`}
                                onClick={() => loadPosts(page)}
                            >
                                {page}
                            </button>
                        </React.Fragment>
                    ))
                }
                
                <button 
                    className="pagination-btn"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => loadPosts(pagination.page + 1)}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        );
    };

    // --- Render Calendar ---
    const renderCalendar = () => {
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDay = firstDay.getDay();
        const daysInMonth = lastDay.getDate();
        
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        
        const days = [];
        
        // Previous month days
        for (let i = startDay - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthLastDay - i),
                otherMonth: true
            });
        }
        
        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: new Date(year, month, i),
                otherMonth: false
            });
        }
        
        // Next month days
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                otherMonth: true
            });
        }
        
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        const today = new Date();
        const isToday = (date) => {
            return date.getDate() === today.getDate() &&
                   date.getMonth() === today.getMonth() &&
                   date.getFullYear() === today.getFullYear();
        };
        
        const getEventsForDay = (date) => {
            return schedules.filter(s => {
                if (!s.scheduled_date) return false;
                try {
                    let y, mo, da;
                    if (s.scheduled_date.includes('T')) {
                        const d = new Date(s.scheduled_date);
                        if (isNaN(d.getTime())) return false;
                        y = d.getFullYear();
                        mo = d.getMonth() + 1;
                        da = d.getDate();
                    } else {
                        const [datePart] = s.scheduled_date.split(' ');
                        [y, mo, da] = datePart.split('-').map(Number);
                    }
                    // Parse as server timezone (LA), get wall clock in user timezone
                    const laDate = fromZonedTime(new Date(y, mo - 1, da, 12, 0, 0), SERVER_TIMEZONE);
                    if (isNaN(laDate.getTime())) return false;
                    const userParts = new Intl.DateTimeFormat('en-CA', {
                        timeZone: userTimezone,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).formatToParts(laDate);
                    const get = (type) => parseInt(userParts.find(p => p.type === type)?.value || '0');
                    return get('day') === date.getDate() &&
                           get('month') - 1 === date.getMonth() &&
                           get('year') === date.getFullYear();
                } catch (e) {
                    return false;
                }
            });
        };
        
        return (
            <div className="social-calendar">
                <div className="calendar-header">
                    <button className="calendar-nav-btn" onClick={prevMonth}>
                        <ChevronLeft size={16} />
                    </button>
                    <h3>{calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                    <button className="calendar-nav-btn" onClick={nextMonth}>
                        <ChevronRight size={16} />
                    </button>
                </div>
                
                <div className="calendar-grid">
                    {dayNames.map(day => (
                        <div key={day} className="calendar-day-header">{day}</div>
                    ))}
                    
                    {days.map((day, index) => {
                        const events = getEventsForDay(day.date);
                        return (
                            <div 
                                key={index} 
                                className={`calendar-day ${day.otherMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''}`}
                            >
                                <div className="calendar-day-number">{day.date.getDate()}</div>
                                {events.slice(0, 2).map((event, i) => (
                                    <div key={i} className={`calendar-event ${event.post_type.toLowerCase()}`}>
                                        {event.property_name}
                                    </div>
                                ))}
                                {events.length > 2 && (
                                    <div className="calendar-event" style={{ backgroundColor: '#e2e8f0', color: '#475569' }}>
                                        +{events.length - 2} more
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // --- Render Create Post Modal ---
    const renderCreateModal = () => {
        if (!showCreateModal) return null;
        
        return (
            <CreatePostModal
                properties={properties}
                selectedPropertyId={selectedPropertyId}
                userTimezone={userTimezone}
                schedules={schedules}
                onClose={() => setShowCreateModal(false)}
                onCreated={handlePostCreated}
            />
        );
    };

    // --- Render Detail Modal ---
    const renderDetailModal = () => {
        if (!showDetailModal || !selectedPost) return null;
        
        return (
            <PostDetailModal
                post={selectedPost}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedPost(null);
                }}
            />
        );
    };

    // --- Render Edit Modal ---
    const renderEditModal = () => {
        if (!showEditModal || !selectedPost) return null;
        
        return (
            <EditPostModal
                post={selectedPost}
                properties={properties}
                userTimezone={userTimezone}
                schedules={schedules}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedPost(null);
                }}
                onUpdated={() => {
                    setShowEditModal(false);
                    setSelectedPost(null);
                    loadPosts(pagination.page);
                    loadSchedules();
                    loadStats();
                    toast.success("Post updated successfully");
                }}
            />
        );
    };

    // --- Render Delete Modal ---
    const renderDeleteModal = () => {
        if (!showDeleteModal || !postToDelete) return null;
        
        return (
            <div className="social-modal-overlay" onClick={() => { setShowDeleteModal(false); setPostToDelete(null); }}>
                <div className="social-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                    <div className="social-modal-header">
                        <h2 style={{ color: '#dc2626' }}>
                            <AlertTriangle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Confirm Deletion
                        </h2>
                        <button className="social-modal-close" onClick={() => { setShowDeleteModal(false); setPostToDelete(null); }}>
                            ×
                        </button>
                    </div>
                    
                    <div className="social-modal-body">
                        <p style={{ marginBottom: '0.5rem' }}>
                            Are you sure you want to delete this <strong>{postToDelete.post_type}</strong> post?
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            {postToDelete.caption?.substring(0, 100)}{postToDelete.caption?.length > 100 ? '...' : ''}
                        </p>
                        <p style={{ color: '#9ca3af', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                            This action cannot be undone.
                        </p>
                    </div>
                    
                    <div className="social-modal-footer">
                        <button 
                            type="button" 
                            className="action-btn"
                            onClick={() => { setShowDeleteModal(false); setPostToDelete(null); }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="create-post-btn"
                            style={{ backgroundColor: '#dc2626' }}
                            onClick={confirmDeletePost}
                        >
                            <Trash2 size={16} />
                            Delete Post
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Render Help Section ---
    const renderHelp = () => {
        if (!showHelp) return null;
        
        return (
            <div className="social-help-section">
                <div className="help-header">
                    <h3>How Social Posting Works</h3>
                    <button className="help-close" onClick={() => setShowHelp(false)}>×</button>
                </div>
                <div className="help-content">
                    <div className="help-item">
                        <span className="help-label">1. Create a Post</span>
                        <p>Click <strong>Create Post</strong>, select a property and post type. Images load automatically from the property's website. Caption and hashtags are generated for you.</p>
                    </div>
                    <div className="help-item">
                        <span className="help-label">2. Save as Draft or Schedule</span>
                        <p><strong>Draft</strong> — Saved but not published yet. You can edit or delete it anytime.<br/>
                        <strong>Scheduled</strong> — Set a date and time. The post will publish automatically to Facebook.</p>
                    </div>
                    <div className="help-item">
                        <span className="help-label">3. Published to Facebook</span>
                        <p>When a scheduled post runs, it publishes to your Facebook page. The status changes to <strong>Posted</strong>. If something goes wrong, it shows <strong>Failed</strong>.</p>
                    </div>
                    <div className="help-item">
                        <span className="help-label">Post Types</span>
                        <p><strong>Room</strong> — Showcase rooms &amp; amenities<br/>
                        <strong>Lobby</strong> — Highlight common areas<br/>
                        <strong>Offer</strong> — Promote deals &amp; discounts<br/>
                        <strong>Attraction</strong> — Feature local spots<br/>
                        <strong>Review</strong> — Share guest testimonials<br/>
                        <strong>Seasonal</strong> — Holiday &amp; seasonal content</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <h1 className="page-title">Social Media Dashboard</h1>
            
            {renderHelp()}
            {renderStats()}
            {renderFilterBar()}
            
            <div className="social-tabs">
                <button 
                    className={`social-tab ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('posts')}
                >
                    Posts
                    <span className="badge">{stats.total_posts || 0}</span>
                </button>
                <button 
                    className={`social-tab ${activeTab === 'scheduled' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scheduled')}
                >
                    Scheduled
                    <span className="badge">{stats.scheduled || 0}</span>
                </button>
                <button 
                    className={`social-tab ${activeTab === 'calendar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calendar')}
                >
                    Calendar
                </button>
            </div>
            
            {activeTab === 'posts' && (
                <>
                    {renderPostsTable()}
                    {renderPagination()}
                </>
            )}
            
            {activeTab === 'scheduled' && (
                <>
                    {loading ? (
                        <div className="social-loading">Loading scheduled posts...</div>
                    ) : schedules.length === 0 ? (
                        <div className="social-empty-state">
                            <Calendar size={48} className="icon" />
                            <h3>No scheduled posts</h3>
                            <p>Create a post and schedule it for automatic publishing.</p>
                        </div>
                    ) : (
                        <div className="social-table-wrapper">
                        <table className="social-posts-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Property</th>
                                    <th>Type</th>
                                    <th>Caption</th>
                                    <th>Scheduled Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedules.map(post => (
                                    <tr key={post.id}>
                                        <td>
                                            <img 
                                                src={post.image_path?.startsWith('[') ? JSON.parse(post.image_path)[0] : post.image_path} 
                                                alt="" 
                                                className="post-thumbnail"
                                                onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                                            />
                                        </td>
                                        <td>{post.property_name}</td>
                                        <td>
                                            <span className={`calendar-event ${post.post_type.toLowerCase()}`}>
                                                {post.post_type}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {post.caption}
                                            </div>
                                        </td>
                                        <td>
                                            {formatDatetime(post.scheduled_date)}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button 
                                                    className="action-btn view"
                                                    onClick={() => handleViewPost(post)}
                                                >
                                                    <Eye size={14} />
                                                </button>
                                                <button 
                                                    className="action-btn edit"
                                                    onClick={() => handleEditPost(post)}
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button 
                                                    className="action-btn delete"
                                                    onClick={() => handleDeletePost(post)}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </>
            )}
            
            {activeTab === 'calendar' && renderCalendar()}
            
            {renderCreateModal()}
            {renderDetailModal()}
            {renderEditModal()}
            {renderDeleteModal()}
        </div>
    );
};

// ============================================================
// Image Picker Item Component (with error handling)
// ============================================================
const ImagePickerItem = ({ img, isSelected, onSelect }) => {
    const [loadState, setLoadState] = useState('loading'); // loading, loaded, error
    
    return (
        <div 
            className={`image-picker-item ${isSelected ? 'selected' : ''} ${loadState === 'error' ? 'no-image' : ''}`}
            onClick={() => onSelect(img.url)}
        >
            {loadState === 'loading' && (
                <div className="image-placeholder">
                    <Image size={20} />
                </div>
            )}
            {loadState === 'error' && (
                <div className="image-placeholder error">
                    <Image size={20} />
                    <span>Preview not available</span>
                </div>
            )}
            <img 
                src={img.url} 
                alt={img.name}
                style={{ display: loadState === 'loaded' ? 'block' : 'none' }}
                onLoad={() => setLoadState('loaded')}
                onError={() => setLoadState('error')}
            />
            <span className="image-name">{img.name}</span>
        </div>
    );
};

// ============================================================
// Create Post Modal Component
// ============================================================
const CreatePostModal = ({ properties, selectedPropertyId, onClose, onCreated, userTimezone, schedules }) => {
    const [formData, setFormData] = useState({
        property_id: selectedPropertyId || '',
        post_type: 'ROOM',
        image_paths: [], // Changed to array for multiple images
        caption: '',
        hashtags: '',
        status: 'DRAFT',
        scheduled_date: '',
        scheduled_time: '10:00'
    });
    const [loading, setLoading] = useState(false);
    const [availableImages, setAvailableImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [activeImageTab, setActiveImageTab] = useState('website');
    const [dateConflict, setDateConflict] = useState('');

    const MAX_IMAGES = 5;

    // Check for date conflicts when date or property changes
    useEffect(() => {
        if (formData.status !== 'SCHEDULED' || !formData.scheduled_date || !formData.property_id) {
            setDateConflict('');
            return;
        }
        const hasConflict = schedules.some(s => {
            if (s.property_id != formData.property_id) return false;
            if (!s.scheduled_date) return false;
            const sDate = s.scheduled_date.split(' ')[0];
            return sDate === formData.scheduled_date;
        });
        setDateConflict(hasConflict ? 'This property already has a post scheduled for this date.' : '');
    }, [formData.scheduled_date, formData.property_id, formData.status, schedules]);

    // Load available images from website when property changes
    useEffect(() => {
        const loadImages = async () => {
            if (!formData.property_id) {
                setAvailableImages([]);
                return;
            }
            
            setLoadingImages(true);
            try {
                const data = await api.getSocialImages(formData.property_id);
                setAvailableImages(data.images || []);
            } catch (err) {
                console.error("Failed to load images:", err);
                setAvailableImages([]);
            } finally {
                setLoadingImages(false);
            }
        };
        
        loadImages();
    }, [formData.property_id]);

    // Generate caption when property or post type changes
    useEffect(() => {
        if (!formData.property_id || !formData.post_type) return;
        
        // Get property details
        const property = properties.find(p => p.id == formData.property_id);
        if (!property) return;
        
        // Generate caption using frontend utility
        const caption = generateCaption(property, formData.post_type, property.city);
        const hashtags = generatePostHashtags(property, formData.post_type, property.city);
        
        setFormData(prev => ({ 
            ...prev, 
            caption: caption, 
            hashtags: hashtags 
        }));
    }, [formData.property_id, formData.post_type, properties]);

    const handleImageSelect = (imageUrl) => {
        setFormData(prev => {
            const images = prev.image_paths || [];
            
            // If already selected, remove it
            if (images.includes(imageUrl)) {
                return { ...prev, image_paths: images.filter(i => i !== imageUrl) };
            }
            
            // If not at max, add it
            if (images.length < MAX_IMAGES) {
                return { ...prev, image_paths: [...images, imageUrl] };
            }
            
            // At max, show warning
            toast.error(`Maximum ${MAX_IMAGES} images allowed`);
            return prev;
        });
    };

    const handleLocalUpload = (e) => {
        const files = Array.from(e.target.files);
        const remainingSlots = MAX_IMAGES - (formData.image_paths?.length || 0);
        
        if (files.length > remainingSlots) {
            toast.error(`Can only add ${remainingSlots} more images`);
            return;
        }
        
        const newUrls = files.map(file => URL.createObjectURL(file));
        setFormData(prev => ({ 
            ...prev, 
            image_paths: [...(prev.image_paths || []), ...newUrls]
        }));
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            image_paths: prev.image_paths.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // Convert user timezone date/time back to server timezone (LA)
            let serverDate = formData.scheduled_date;
            let serverTime = formData.scheduled_time;
            if (formData.status === 'SCHEDULED' && formData.scheduled_date && formData.scheduled_time) {
                const [y, mo, da] = formData.scheduled_date.split('-').map(Number);
                const [h, mi] = formData.scheduled_time.split(':').map(Number);
                // Parse user input as their timezone
                const userInstant = fromZonedTime(new Date(y, mo - 1, da, h, mi), userTimezone);
                // Get wall clock time in LA
                const laParts = new Intl.DateTimeFormat('en-CA', {
                    timeZone: SERVER_TIMEZONE,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false
                }).formatToParts(userInstant);
                const get = (type) => laParts.find(p => p.type === type)?.value || '';
                serverDate = `${get('year')}-${get('month')}-${get('day')}`;
                serverTime = `${get('hour')}:${get('minute')}`;
            }

            const postData = {
                ...formData,
                scheduled_date: serverDate,
                scheduled_time: serverTime,
                image_path: formData.image_paths?.length > 0 
                    ? JSON.stringify(formData.image_paths) 
                    : formData.image_paths?.[0] || ''
            };
            await api.createSocialPost(postData);
            onCreated();
        } catch (err) {
            toast.error(err.message || "Failed to create post");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="social-modal-overlay" onClick={onClose}>
            <div className="social-modal" onClick={e => e.stopPropagation()}>
                <div className="social-modal-header">
                    <h2>Create Post</h2>
                    <button className="social-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="social-modal-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Property</label>
                                <select 
                                    value={formData.property_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, property_id: e.target.value }))}
                                    required
                                >
                                    <option value="">Select Property</option>
                                    {properties.map(prop => (
                                        <option key={prop.id} value={prop.id}>{prop.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Post Type</label>
                                <select 
                                    value={formData.post_type}
                                    onChange={(e) => setFormData(prev => ({ ...prev, post_type: e.target.value }))}
                                    required
                                >
                                    <option value="ROOM">Room</option>
                                    <option value="LOBBY">Lobby</option>
                                    <option value="OFFER">Offer</option>
                                    <option value="ATTRACTION">Attraction</option>
                                    <option value="REVIEW">Review</option>
                                    <option value="SEASONAL">Seasonal</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Image Selection */}
                        <div className="form-group">
                            <label>Images ({formData.image_paths?.length || 0}/{MAX_IMAGES} selected)</label>
                            <div className="image-tabs">
                                <button 
                                    type="button"
                                    className={`image-tab ${activeImageTab === 'website' ? 'active' : ''}`}
                                    onClick={() => setActiveImageTab('website')}
                                >
                                    Website Images
                                </button>
                                <button 
                                    type="button"
                                    className={`image-tab ${activeImageTab === 'upload' ? 'active' : ''}`}
                                    onClick={() => setActiveImageTab('upload')}
                                >
                                    Upload New
                                </button>
                            </div>
                            
                            {activeImageTab === 'website' && (
                                <div className="image-picker-container">
                                    {loadingImages ? (
                                        <div className="image-loading">Loading images...</div>
                                    ) : availableImages.length > 0 ? (
                                        <div className="image-picker-grid">
                                            {availableImages.map((img, index) => (
                                                <ImagePickerItem 
                                                    key={index}
                                                    img={img}
                                                    isSelected={formData.image_paths?.includes(img.url)}
                                                    onSelect={handleImageSelect}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="image-empty">
                                            {formData.property_id ? 'No images found for this property' : 'Select a property first'}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {activeImageTab === 'upload' && (
                                <div className="image-upload-container">
                                    <input 
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleLocalUpload}
                                        id="image-upload"
                                        className="file-input"
                                    />
                                    <label htmlFor="image-upload" className="file-label">
                                        <Image size={24} />
                                        <span>Choose from your computer (max {MAX_IMAGES})</span>
                                    </label>
                                </div>
                            )}
                            
                            {/* Selected Images Preview */}
                            {formData.image_paths?.length > 0 && (
                                <div className="selected-images-preview">
                                    <label>Selected Images:</label>
                                    <div className="selected-images-grid">
                                        {formData.image_paths.map((url, index) => (
                                            <div key={index} className="selected-image-item">
                                                <img src={url} alt={`Selected ${index + 1}`} />
                                                <button 
                                                    type="button"
                                                    className="remove-image-btn"
                                                    onClick={() => removeImage(index)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label>Caption</label>
                            <textarea
                                value={formData.caption}
                                onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                                placeholder="Write your caption..."
                                rows={4}
                            />
                            <button 
                                type="button"
                                className="regenerate-btn"
                                onClick={() => {
                                    const property = properties.find(p => p.id == formData.property_id);
                                    if (!property) {
                                        toast.error("Select a property first");
                                        return;
                                    }
                                    const caption = generateCaption(property, formData.post_type, property.city);
                                    const hashtags = generatePostHashtags(property, formData.post_type, property.city);
                                    setFormData(prev => ({ 
                                        ...prev, 
                                        caption, 
                                        hashtags 
                                    }));
                                    toast.success("Caption regenerated");
                                }}
                            >
                                <RefreshCw size={14} /> Regenerate
                            </button>
                        </div>
                        
                        <div className="form-group">
                            <label>Hashtags</label>
                            <input 
                                type="text"
                                value={formData.hashtags}
                                onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                                placeholder="#HotelName #City #HotelDeals"
                            />
                        </div>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>Status</label>
                                <select 
                                    value={formData.status}
                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <option value="DRAFT">Draft</option>
                                    <option value="SCHEDULED">Scheduled</option>
                                </select>
                            </div>
                            
                            {formData.status === 'SCHEDULED' && (
                                <>
                                    <div className="form-group">
                                        <label>Scheduled Date</label>
                                        <input 
                                            type="date"
                                            value={formData.scheduled_date}
                                            min={new Date().toLocaleDateString('en-CA', { timeZone: userTimezone })}
                                            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                                            required
                                        />
                                        {dateConflict && (
                                            <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>{dateConflict}</p>
                                        )}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Scheduled Time</label>
                                        <input 
                                            type="time"
                                            value={formData.scheduled_time}
                                            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                                            required
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {/* Caption Preview */}
                        {formData.caption && (
                            <div className="caption-preview">
                                <p><strong>Preview:</strong></p>
                                <p>{formData.caption}</p>
                                {formData.hashtags && (
                                    <p className="hashtags">{formData.hashtags}</p>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="social-modal-footer">
                        <button 
                            type="button" 
                            className="action-btn"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="create-post-btn"
                            disabled={loading || !!dateConflict}
                        >
                            {loading ? 'Creating...' : 'Save Post'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================================
// Post Detail Modal Component
// ============================================================
const PostDetailModal = ({ post, onClose }) => {
    return (
        <div className="social-modal-overlay" onClick={onClose}>
            <div className="social-modal" onClick={e => e.stopPropagation()}>
                <div className="social-modal-header">
                    <h2>Post Details</h2>
                    <button className="social-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                
                <div className="social-modal-body">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Property</label>
                            <p>{post.property_name}</p>
                        </div>
                        <div className="form-group">
                            <label>Post Type</label>
                            <p>
                                <span className={`calendar-event ${post.post_type.toLowerCase()}`}>
                                    {post.post_type}
                                </span>
                            </p>
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>Status</label>
                            <p>
                                <span className={`status-badge ${post.status.toLowerCase()}`}>
                                    {post.status}
                                </span>
                            </p>
                        </div>
                        <div className="form-group">
                            <label>Created</label>
                            <p>{new Date(post.created_at).toLocaleString()}</p>
                        </div>
                    </div>
                    
                    {post.posted_at && (
                        <div className="form-group">
                            <label>Posted At</label>
                            <p>{new Date(post.posted_at).toLocaleString()}</p>
                        </div>
                    )}
                    
                    {post.facebook_post_id && (
                        <div className="form-group">
                            <label>Facebook Post ID</label>
                            <p>{post.facebook_post_id}</p>
                        </div>
                    )}
                    
                    {post.image_path && (
                        <div className="form-group">
                            <label>Images</label>
                            <div className="selected-images-grid">
                                {(post.image_path.startsWith('[') ? JSON.parse(post.image_path) : [post.image_path]).map((url, index) => (
                                    <img 
                                        key={index}
                                        src={url} 
                                        alt={`Post image ${index + 1}`}
                                        style={{ maxWidth: '200px', borderRadius: '0.5rem', marginTop: '0.5rem', marginRight: '0.5rem' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label>Caption</label>
                        <div className="caption-preview">
                            <p>{post.caption}</p>
                        </div>
                    </div>
                    
                    {post.hashtags && (
                        <div className="form-group">
                            <label>Hashtags</label>
                            <p className="hashtags" style={{ color: '#1798C1' }}>{post.hashtags}</p>
                        </div>
                    )}
                </div>
                
                <div className="social-modal-footer">
                    <button 
                        type="button" 
                        className="action-btn"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Edit Post Modal Component
// ============================================================
const EditPostModal = ({ post, properties, onClose, onUpdated, userTimezone, schedules }) => {
    const parseDatetime = (dtStr) => {
        if (!dtStr) return { date: '', time: '10:00' };
        try {
            let y, mo, da, h, mi;
            if (dtStr.includes('T')) {
                const d = new Date(dtStr);
                if (isNaN(d.getTime())) return { date: '', time: '10:00' };
                y = d.getFullYear();
                mo = d.getMonth() + 1;
                da = d.getDate();
                h = d.getHours();
                mi = d.getMinutes();
            } else {
                const [datePart, timePart] = dtStr.split(' ');
                [y, mo, da] = datePart.split('-').map(Number);
                [h, mi] = (timePart || '10:00').split(':').map(Number);
            }
            // Parse as server timezone (LA), get wall clock in user timezone
            const laDate = fromZonedTime(new Date(y, mo - 1, da, h, mi), SERVER_TIMEZONE);
            if (isNaN(laDate.getTime())) return { date: '', time: '10:00' };
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: userTimezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).formatToParts(laDate);
            const get = (type) => parts.find(p => p.type === type)?.value || '';
            return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
        } catch (e) {
            return { date: '', time: '10:00' };
        }
    };
    const parsed = parseDatetime(post.scheduled_date);

    const [formData, setFormData] = useState({
        property_id: post.property_id,
        post_type: post.post_type,
        image_paths: post.image_path?.startsWith('[') ? JSON.parse(post.image_path) : (post.image_path ? [post.image_path] : []),
        caption: post.caption || '',
        hashtags: post.hashtags || '',
        status: post.status,
        scheduled_date: parsed.date,
        scheduled_time: parsed.time
    });
    const [loading, setLoading] = useState(false);
    const [availableImages, setAvailableImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [activeImageTab, setActiveImageTab] = useState('website');
    const [dateConflict, setDateConflict] = useState('');

    const MAX_IMAGES = 5;

    // Check for date conflicts when date or property changes
    useEffect(() => {
        if (formData.status !== 'SCHEDULED' || !formData.scheduled_date || !formData.property_id) {
            setDateConflict('');
            return;
        }
        const hasConflict = schedules.some(s => {
            if (s.property_id != formData.property_id) return false;
            if (s.id === post.id) return false; // Exclude current post
            if (!s.scheduled_date) return false;
            const sDate = s.scheduled_date.split(' ')[0];
            return sDate === formData.scheduled_date;
        });
        setDateConflict(hasConflict ? 'This property already has a post scheduled for this date.' : '');
    }, [formData.scheduled_date, formData.property_id, formData.status, schedules, post.id]);

    // Load available images when component mounts
    useEffect(() => {
        const loadImages = async () => {
            if (!formData.property_id) return;
            
            setLoadingImages(true);
            try {
                const data = await api.getSocialImages(formData.property_id);
                setAvailableImages(data.images || []);
            } catch (err) {
                console.error("Failed to load images:", err);
            } finally {
                setLoadingImages(false);
            }
        };
        loadImages();
    }, [formData.property_id]);

    const handleImageSelect = (imageUrl) => {
        setFormData(prev => {
            const images = prev.image_paths || [];
            if (images.includes(imageUrl)) {
                return { ...prev, image_paths: images.filter(i => i !== imageUrl) };
            }
            if (images.length < MAX_IMAGES) {
                return { ...prev, image_paths: [...images, imageUrl] };
            }
            toast.error(`Maximum ${MAX_IMAGES} images allowed`);
            return prev;
        });
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            image_paths: prev.image_paths.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // Convert user timezone date/time back to server timezone (LA)
            let serverDate = formData.scheduled_date;
            let serverTime = formData.scheduled_time;
            if (formData.status === 'SCHEDULED' && formData.scheduled_date && formData.scheduled_time) {
                const [y, mo, da] = formData.scheduled_date.split('-').map(Number);
                const [h, mi] = formData.scheduled_time.split(':').map(Number);
                // Parse user input as their timezone
                const userInstant = fromZonedTime(new Date(y, mo - 1, da, h, mi), userTimezone);
                // Get wall clock time in LA
                const laParts = new Intl.DateTimeFormat('en-CA', {
                    timeZone: SERVER_TIMEZONE,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false
                }).formatToParts(userInstant);
                const get = (type) => laParts.find(p => p.type === type)?.value || '';
                serverDate = `${get('year')}-${get('month')}-${get('day')}`;
                serverTime = `${get('hour')}:${get('minute')}`;
            }

            const postData = {
                ...formData,
                scheduled_date: serverDate,
                scheduled_time: serverTime,
                image_path: formData.image_paths?.length > 0 
                    ? JSON.stringify(formData.image_paths) 
                    : ''
            };
            await api.updateSocialPost(post.id, postData);
            onUpdated();
        } catch (err) {
            toast.error(err.message || "Failed to update post");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="social-modal-overlay" onClick={onClose}>
            <div className="social-modal" onClick={e => e.stopPropagation()}>
                <div className="social-modal-header">
                    <h2>Edit Post</h2>
                    <button className="social-modal-close" onClick={onClose}>×</button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="social-modal-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Property</label>
                                <select 
                                    value={formData.property_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, property_id: e.target.value }))}
                                    required
                                >
                                    <option value="">Select Property</option>
                                    {properties.map(prop => (
                                        <option key={prop.id} value={prop.id}>{prop.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Post Type</label>
                                <select 
                                    value={formData.post_type}
                                    onChange={(e) => setFormData(prev => ({ ...prev, post_type: e.target.value }))}
                                    required
                                >
                                    <option value="ROOM">Room</option>
                                    <option value="LOBBY">Lobby</option>
                                    <option value="OFFER">Offer</option>
                                    <option value="ATTRACTION">Attraction</option>
                                    <option value="REVIEW">Review</option>
                                    <option value="SEASONAL">Seasonal</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Image Selection */}
                        <div className="form-group">
                            <label>Images ({formData.image_paths?.length || 0}/{MAX_IMAGES} selected)</label>
                            <div className="image-tabs">
                                <button 
                                    type="button"
                                    className={`image-tab ${activeImageTab === 'website' ? 'active' : ''}`}
                                    onClick={() => setActiveImageTab('website')}
                                >
                                    Website Images
                                </button>
                            </div>
                            
                            {activeImageTab === 'website' && (
                                <div className="image-picker-container">
                                    {loadingImages ? (
                                        <div className="image-loading">Loading images...</div>
                                    ) : availableImages.length > 0 ? (
                                        <div className="image-picker-grid">
                                            {availableImages.map((img, index) => (
                                                <ImagePickerItem 
                                                    key={index}
                                                    img={img}
                                                    isSelected={formData.image_paths?.includes(img.url)}
                                                    onSelect={handleImageSelect}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="image-empty">No images found for this property</div>
                                    )}
                                </div>
                            )}
                            
                            {/* Selected Images Preview */}
                            {formData.image_paths?.length > 0 && (
                                <div className="selected-images-preview">
                                    <label>Selected Images:</label>
                                    <div className="selected-images-grid">
                                        {formData.image_paths.map((url, index) => (
                                            <div key={index} className="selected-image-item">
                                                <img src={url} alt={`Selected ${index + 1}`} />
                                                <button 
                                                    type="button"
                                                    className="remove-image-btn"
                                                    onClick={() => removeImage(index)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label>Caption</label>
                            <textarea
                                value={formData.caption}
                                onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                                placeholder="Write your caption..."
                                rows={4}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Hashtags</label>
                            <input 
                                type="text"
                                value={formData.hashtags}
                                onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                                placeholder="#HotelName #City #HotelDeals"
                            />
                        </div>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>Status</label>
                                <select 
                                    value={formData.status}
                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <option value="DRAFT">Draft</option>
                                    <option value="SCHEDULED">Scheduled</option>
                                    <option value="FAILED">Failed</option>
                                </select>
                            </div>
                            
                            {formData.status === 'SCHEDULED' && (
                                <>
                                    <div className="form-group">
                                        <label>Scheduled Date</label>
                                        <input 
                                            type="date"
                                            value={formData.scheduled_date}
                                            min={new Date().toLocaleDateString('en-CA', { timeZone: userTimezone })}
                                            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                                            required
                                        />
                                        {dateConflict && (
                                            <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>{dateConflict}</p>
                                        )}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Scheduled Time</label>
                                        <input 
                                            type="time"
                                            value={formData.scheduled_time}
                                            onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                                            required
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    
                    <div className="social-modal-footer">
                        <button 
                            type="button" 
                            className="action-btn"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="create-post-btn"
                            disabled={loading || !!dateConflict}
                        >
                            {loading ? 'Updating...' : 'Update Post'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SocialScreen;
